import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getBackendBaseUrl } from '../lib/apiBase';

const API = `${getBackendBaseUrl()}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('profitspot_token'));
  const [loading, setLoading] = useState(true);

  // Load user on mount
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          // Token invalid, clear it
          localStorage.removeItem('profitspot_token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    loadUser();
  }, [token]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('profitspot_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (email, password, name, referral_code) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, referral_code });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('profitspot_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('profitspot_token');
    setToken(null);
    setUser(null);
  }, []);

  const updateWallet = useCallback(async (walletAddress) => {
    if (!token) return;
    await axios.put(`${API}/auth/wallet?wallet_address=${walletAddress}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(prev => ({ ...prev, wallet_address: walletAddress }));
  }, [token]);

  const saveOpportunity = useCallback(async (opportunityId) => {
    if (!token) return;
    await axios.post(`${API}/auth/save-opportunity/${opportunityId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(prev => ({
      ...prev,
      saved_opportunities: [...(prev.saved_opportunities || []), opportunityId]
    }));
  }, [token]);

  const unsaveOpportunity = useCallback(async (opportunityId) => {
    if (!token) return;
    await axios.delete(`${API}/auth/save-opportunity/${opportunityId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(prev => ({
      ...prev,
      saved_opportunities: (prev.saved_opportunities || []).filter(id => id !== opportunityId)
    }));
  }, [token]);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, [token]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateWallet,
    saveOpportunity,
    unsaveOpportunity,
    refreshUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
